// MatomoContext.tsx
import { createContext, useContext, useEffect, ReactNode, useRef } from 'react'

type MatomoEvent = {
  category: string
  action: string
  name?: string
}

type MatomoContextValue = {
  trackEvent: (event: MatomoEvent) => void
  trackPageView: (title?: string, url?: string) => void
}

type MatomoProviderProps = {
  children: ReactNode
}

declare global {
  interface Window {
    _paq?: any[][]
    _matomoInitialized?: boolean
  }
}

const MatomoContext = createContext<MatomoContextValue | null>(null)

export const MatomoProvider = ({ children }: MatomoProviderProps) => {
  const initialized = useRef(false)
  const siteId = process.env.REACT_APP_MATOMO_SITE_ID

  useEffect(() => {
    if (initialized.current || typeof window === 'undefined' || window._matomoInitialized) {
      return
    }

    initialized.current = true
    window._matomoInitialized = true

    const _paq = (window._paq = window._paq || [])

    if (!_paq.some((cmd) => cmd[0] === 'setTrackerUrl')) {
      _paq.push(['setTrackerUrl', 'https://dhigroup.matomo.cloud/matomo.php'])
    }

    if (!_paq.some((cmd) => cmd[0] === 'setSiteId')) {
      _paq.push(['setSiteId', siteId])
    }

    _paq.push(['enableLinkTracking'])

    const matomoScriptId = 'matomo-tracker-script'
    if (!document.getElementById(matomoScriptId)) {
      const script = document.createElement('script')
      script.id = matomoScriptId
      script.async = true
      script.src = 'https://cdn.matomo.cloud/dhigroup.matomo.cloud/matomo.js'
      document.head.appendChild(script)
    }
  }, [])

  const trackEvent = ({ category, action, name }: MatomoEvent) => {
    if (!window._paq) {
      console.warn('Matomo tracker not initialized')
      return
    }
    window._paq.push(['trackEvent', category, action, name])
  }

  const trackPageView = (title?: string, url?: string) => {
    if (!window._paq) {
      console.warn('Matomo tracker not initialized')
      return
    }
    window._paq.push(['setDocumentTitle', title || document.title])
    window._paq.push(['setCustomUrl', url || window.location.href])
    window._paq.push(['trackPageView'])
  }

  const value: MatomoContextValue = {
    trackEvent,
    trackPageView,
  }

  return <MatomoContext.Provider value={value}>{children}</MatomoContext.Provider>
}

export function useMatomo(): MatomoContextValue {
  const context = useContext(MatomoContext)
  if (!context) {
    throw new Error('useMatomo must be used within a MatomoProvider')
  }
  return context
}
